export type OrbState = 'dormant' | 'idle' | 'listening' | 'thinking' | 'responding' | 'error';
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error'
}
export type AgentState = 'idle' | 'listening' | 'processing' | 'speaking';
// Orbi can run in three modes:
// - 'customer': End user calling in (identified by phone, warm/simple UX)
// - 'agent': Support agent using Orbi as a copilot (searches by account/name, gets full diagnostics)
// - 'operator': The platform operator copilot. NOT a customer and NOT the NOC
//   field tech. Orbi speaks to staff about what is on screen and across the
//   platform; read-only; never asks for an account number, never greets a customer.
export type OrbiMode = 'customer' | 'agent' | 'operator';
export interface LogMessage {
  id: string;
  timestamp: Date;
  role: 'user' | 'assistant' | 'system' | 'tool';
  text: string;
}
export interface CustomerContext {
  account_number: string;
  first_name: string;
  last_name: string;
  plan_name: string;
  plan_speed_mbps: number;
  current_speed_mbps: number;
  connection_status: string;
  devices?: string[];
  devices_online: number;
  billing_status: string;
  port_id: string | null;
  switch_id: string | null;
  diagnostics: Record<string, any>;
  vertical_domain?: string;
  persona_greeting?: string;
  /**
   * billing_context sources:
   *  - WHMCS / Sonar: fetched from the billing-cache tables via fetchBillingContext
   *  - ANDREW: lifted directly from Andrew's n8n webhook payload by
   *    mapAndrewResponse when the webhook already exposes invoice_amount
   *    (e.g. demo-suspended), so PPA-007 can state the exact R amount
   *    without a cache row being present.
   */
  billing_context?: {
    source: 'WHMCS' | 'Sonar' | 'ANDREW';
    invoice_amount?: number;
    invoice_days_overdue?: number;
    next_due_date?: string;
    payment_method_on_file?: string;
    data_used_gb?: number;
    data_cap_gb?: number;
    last_seen?: string;
    job_scheduled?: boolean;
    job_date?: string;
    currency?: string;
  } | null;
  open_tickets: Array<{
    ticket_number: string;
    subject: string;
    status: string;
    created_at: string;
  }>;
  skills?: Array<{
    name: string;
    description: string;
    webhook_url: string;
    parameters: Record<string, any>;
    is_primary?: boolean;
    /** Whether this skill is live — forwarded from Supabase so PPA-007
     *  and other prompt blocks can check operator approval before firing. */
    enabled?: boolean;
  }>;
  persona?: {
    persona_key: string;
    title: string;
    subtitle: string;
    greeting: string;
    conversation_starters: string[];
  } | null;
  agent?: {
    name: string;
    description: string;
    system_prompt: string;
    division: string;
    quality_gates: Record<string, any>;
  } | null;
  /**
   * True when fetchCustomerContext fell through to hardcoded demo data
   * because every live source (Andrew webhook + Supabase fallback) failed.
   * Drives the degraded-state banner in VoiceOverlay. Not set when
   * VITE_DEMO_MODE is on (that's intentional, not degraded).
   */
  _demoFallback?: boolean;
  /**
   * Lead routing config. No longer resolved in the browser (SEC-1 step d,
   * 2026-10-21): the field is never set, so the useVapi branch gated on it
   * is dormant. lead_capture_v1 is composed server-side by orbi-context-fetch.
   */
  lead_routing?: {
    altitude_level: 'ORA' | 'VERTICAL' | 'CLIENT';
    client_id: string | null;
    vertical_id: string | null;
    webhook_url: string;
    dashboard_origin: string;
    notification_recipients: Array<{ label?: string; address: string; channel: string }>;
    staging_mode: boolean;
  } | null;
  memory?: {
    recent_sessions: Array<{
      conversation_summary: string;
      actions_taken: Array<{ skill: string; result?: string; success: boolean }>;
      resolved: boolean;
      persona_key: string;
      created_at: string;
    }>;
    customer_insights: {
      communication_style: string;
      recurring_issues: string;
      resolution_history: string;
      preferences: string;
      risk_flags: string;
      confidence: number;
      interaction_count: number;
    } | null;
    system_insights: Array<{
      title: string;
      description: string;
      insight_type: string;
      affected_count: number;
    }>;
    agent_learnings?: Array<{
      key_learning: string;
      behavioral_recommendation: string;
      confidence: number;
      approved_by: string;
      created_at: string;
    }>;
  };
}
